import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Package, Square, PackageOpen, Send, Truck, Warehouse, CheckCircle2, QrCode, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const CARDS = [
  { k: "products_total", label: "Total Products", icon: Package, accent: "text-cyan-400" },
  { k: "qr_blank", label: "Total Blank QR Codes", icon: Square, accent: "text-slate-700 dark:text-slate-300" },
  { k: "qr_assigned", label: "Total Assigned QR Codes", icon: PackageOpen, accent: "text-blue-400" },
  { k: "packages_total", label: "Total Packages Created", icon: Send, accent: "text-purple-400" },
  { k: "packages_waiting", label: "Waiting for Pickup", icon: Send, accent: "text-yellow-400" },
  { k: "packages_picked_up", label: "Picked Up by Rider", icon: Truck, accent: "text-blue-400" },
  { k: "packages_received", label: "Received at Warehouse", icon: Warehouse, accent: "text-indigo-400" },
  { k: "packages_delivered", label: "Delivered to Customer", icon: CheckCircle2, accent: "text-green-400" },
];

export default function StoreOverview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/store/stats").then(r => setStats(r.data)).catch(() => setStats({}));
  }, []);

  return (
    <div data-testid="store-overview-page" className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">/ Store Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Today at a glance</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Products, QR codes, and packages — all from one console.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/store/qr-generator">
            <Button data-testid="quick-generate-qr" className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium">
              <QrCode className="w-4 h-4 mr-2" /> Generate QR Codes
            </Button>
          </Link>
          <Link to="/store/create-package">
            <Button data-testid="quick-create-package" variant="outline" className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white">
              <Send className="w-4 h-4 mr-2" /> Create Package
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map(c => (
          <div
            key={c.k}
            data-testid={`store-stat-${c.k}`}
            className="bg-slate-100/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg p-5 hover:border-slate-300 hover:dark:border-slate-700 transition-colors"
          >
            <div className="flex items-center justify-between mb-4">
              <c.icon className={`w-5 h-5 ${c.accent}`} />
              <span className="text-[10px] font-mono-tech text-slate-500 uppercase tracking-wider">LIVE</span>
            </div>
            <div className="font-display text-3xl font-bold">{stats == null ? "—" : (stats[c.k] ?? 0)}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-snug">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/store/qr/blank" className="block group">
          <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-6 bg-slate-100/40 dark:bg-slate-900/40 hover:border-cyan-500/30 transition-colors">
            <Square className="w-6 h-6 text-slate-700 dark:text-slate-300 mb-3" />
            <div className="font-display text-lg font-semibold mb-1">Manage Blank QR Codes</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Assign products, add package info, print labels.</div>
            <div className="inline-flex items-center gap-2 mt-4 text-xs text-cyan-400 font-mono-tech uppercase tracking-wider group-hover:translate-x-1 transition-transform">
              Open <ArrowRight className="w-3 h-3" />
            </div>
          </div>
        </Link>
        <Link to="/store/qr/assigned" className="block group">
          <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-6 bg-slate-100/40 dark:bg-slate-900/40 hover:border-cyan-500/30 transition-colors">
            <PackageOpen className="w-6 h-6 text-blue-400 mb-3" />
            <div className="font-display text-lg font-semibold mb-1">Assigned QR Codes</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">QRs ready for package information entry.</div>
            <div className="inline-flex items-center gap-2 mt-4 text-xs text-cyan-400 font-mono-tech uppercase tracking-wider group-hover:translate-x-1 transition-transform">
              Open <ArrowRight className="w-3 h-3" />
            </div>
          </div>
        </Link>
        <Link to="/store/packages" className="block group">
          <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-6 bg-slate-100/40 dark:bg-slate-900/40 hover:border-cyan-500/30 transition-colors">
            <Send className="w-6 h-6 text-purple-400 mb-3" />
            <div className="font-display text-lg font-semibold mb-1">My Packages</div>
            <div className="text-sm text-slate-600 dark:text-slate-400">Live tracking and cancellation for your shipments.</div>
            <div className="inline-flex items-center gap-2 mt-4 text-xs text-cyan-400 font-mono-tech uppercase tracking-wider group-hover:translate-x-1 transition-transform">
              Open <ArrowRight className="w-3 h-3" />
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
